import React from 'react';
import {
    ApiHandler,
    useDonorProfile,
    useGiftSummary,
    useGiftSummaryFailedOnMount,
} from '../../../api';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../utils/routes';
import { usePushEvents } from '../../../hooks/PushEvents';
import { profileDetailsShown } from '../../../hooks/PushEvents/clevertapEvents';

export const useProfileDetails = () => {
    const navigate = useNavigate();
    const { pushEvent } = usePushEvents();

    React.useEffect(() => {
        pushEvent(profileDetailsShown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const {
        donorProfile: profile,
        isLoading: profileLoading,
        isError: profileError,
    } = useDonorProfile(true);
    const retryFailedGiftSummary = useGiftSummaryFailedOnMount();
    const {
        giftSummary: gift,
        isLoading: isGiftSummaryLoading,
        hasNeverFetched: giftHasNeverFetched,
        isError: giftError,
    } = useGiftSummary(retryFailedGiftSummary);
    const giftLoading = isGiftSummaryLoading || giftHasNeverFetched;

    const onEditProfileClick = React.useCallback(() => {
        navigate(ROUTES.editProfile);
    }, [navigate]);

    const fullAddress = React.useMemo(() => {
        const addr = profile?.donor.address;
        const stateZip = [addr?.state, addr?.zip].filter(Boolean).join(' ');
        return [addr?.street, addr?.city, stateZip].filter(Boolean).join(', ');
    }, [profile?.donor.address]);

    const location = useLocation();
    const hasSavedQuery = React.useMemo(
        () => location.search.includes('saved=true'),
        [location.search],
    );

    const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
    const [passwordChangePending, setPasswordChangePending] =
        React.useState(false);
    const [passwordChangeState, setPasswordChangeState] = React.useState<
        undefined | boolean
    >(undefined);

    const dismissAlert = React.useCallback(() => {
        navigate(ROUTES.profile, { replace: true });
    }, [navigate]);

    const dismissPasswordAlert = React.useCallback(() => {
        setPasswordChangeState(undefined);
    }, []);

    const changePasswordAction = React.useCallback(async () => {
        if (!profile?.donor.email) return;
        setPasswordChangePending(true);
        try {
            const request = await ApiHandler.instance.auth.requestPasswordReset(
                profile.donor.email,
            );
            setChangePasswordOpen(false);
            setPasswordChangeState(request.success);
        } finally {
            setPasswordChangePending(false);
        }
    }, [profile?.donor.email]);

    React.useEffect(() => {
        if (profileError || giftError) {
            navigate(ROUTES.login);
        }
    }, [profileError, giftError, navigate]);

    const metaTags = React.useMemo(
        () => ({
            title: 'Profile | Givelify',
            description: 'Givelify profile view',
        }),
        [],
    );

    return {
        metaTags,
        profile,
        gift,
        profileLoading,
        giftLoading,
        fullAddress,
        hasSavedQuery,
        changePasswordOpen,
        setChangePasswordOpen,
        passwordChangePending,
        passwordChangeState,
        dismissAlert,
        dismissPasswordAlert,
        changePasswordAction,
        onEditProfileClick,
    };
};
